import { useCallback, useEffect, useRef } from "react";

// Default pixels moved per ~60fps frame while hovered; low so the scroll is easy
// to read. Callers with taller content (e.g. email previews) can pass more.
const DEFAULT_PIXELS_PER_FRAME = 0.35;

/**
 * Auto-scrolls a scrollable element up and down while hovered, mirroring the
 * email preview's `useScrollingFrame` behavior for a plain DOM element (e.g.
 * the text broadcast chat-bubble preview).
 */
export function useScrollingElement<T extends HTMLElement>({
  pixelsPerFrame = DEFAULT_PIXELS_PER_FRAME,
}: { pixelsPerFrame?: number } = {}) {
  const elementRef = useRef<T>(null);
  const scrollInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopScrolling = useCallback(() => {
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    stopScrolling();
    let direction = 1;
    // Accumulate a float position rather than reading scrollTop back each tick:
    // browsers round scrollTop to an integer, so a sub-pixel step (e.g. 0.35)
    // read-modify-written would oscillate near zero and never advance.
    let position = elementRef.current?.scrollTop ?? 0;
    scrollInterval.current = setInterval(() => {
      const el = elementRef.current;
      if (!el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 0) return;
      position += direction * pixelsPerFrame;
      if (position >= maxScroll) {
        position = maxScroll;
        direction = -1;
      } else if (position <= 0) {
        position = 0;
        direction = 1;
      }
      el.scrollTop = position;
    }, 16); // ~60fps
  }, [stopScrolling, pixelsPerFrame]);

  const handleMouseLeave = useCallback(() => {
    stopScrolling();
    if (elementRef.current) elementRef.current.scrollTop = 0;
  }, [stopScrolling]);

  // Clear any running interval on unmount.
  useEffect(() => stopScrolling, [stopScrolling]);

  return { elementRef, handleMouseEnter, handleMouseLeave };
}
